import { clsx } from "clsx";
import React, { FunctionComponent, memo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";

import { partTypeTire } from "../../../../constants/partTypes";
import { useAllowPartPricing } from "../../../../hooks/useAllowPartPricing";
import { useLanguage } from "../../../../i18n/useLanguage";
import { VehicleInfo } from "../../../../queries/useGetVehicleInfo";
import {
  ResultParameters,
  TireMetatags,
} from "../../../../queries/useSearchTiresByVehicle";
import { useIsLoggedOn } from "../../../../stores/hooks/useIsLoggedOn";
import { Image } from "../../../../ui/Image/Image";
import { KeyValue } from "../../../../ui/KeyValue/KeyValue";
import { Placeholder } from "../../../../ui/Placeholder";
import { fixTireImageUrl } from "../../../../util/fixTireImageUrl";
import { getPartFallbackSrc } from "../../../../util/getPartFallbackSrc";
import { getPartName } from "../../../../util/getPartName";
import { CartButton } from "../../../Cart/CartButton/CartButton";
import { useStockIsEnabled } from "../../../Cart/useStockIsEnabled";
import { PriceSelect } from "../../../PriceSelect/PriceSelect";
import { usePriceSelectState } from "../../../PriceSelect/usePriceSelectState";
import { getProductModalHash } from "../../../ProductDetails/ProductModal";
import { getTirePartNumber } from "../../Common/getTirePartNumber";
import { partIsStaggered } from "../Filters/TireSizeFilters/partIsStaggered";
import { TireMetatagList } from "../TireMetatagList/TireMetatagList";
import { ModifiedTirePartItem } from "../useTireResults";
import { CompareButton } from "./Compare";
import { Rebates } from "./Rebates";
import { useRibbon } from "./Ribbon/useRibbon";
import { TireStock, countStock, filterStock, formatCount } from "./TireStock";

/**
 * In the three-column layout each Good/Better/Best tile keeps its own column,
 * so an empty slot does not shift Better and Best to the left. The list's
 * dense flow backfills the empty slot with the next tire (TW-1095).
 */
const featuredColumnClassNames: Record<number, string> = {
  1: "xl:col-start-1",
  2: "xl:col-start-2",
  3: "xl:col-start-3",
};

type DealerTireTileProps = {
  index: number;
  isCheckedForCompare: boolean;
  metatagList: TireMetatags | null;
  part: ModifiedTirePartItem | null;
  resultParameters: ResultParameters | null;
  vehicleInfo: VehicleInfo | null;
};

const UncachedTiresTile: FunctionComponent<DealerTireTileProps> = ({
  index,
  isCheckedForCompare,
  metatagList,
  part,
  resultParameters,
  vehicleInfo,
}) => {
  const [priceOption, setPiceOption] = usePriceSelectState([]);
  const [language] = useLanguage();
  const isLoggedOn = useIsLoggedOn();

  const { t } = useTranslation();

  const isStaggered = partIsStaggered(part);

  const allStock = part?.partStock.primaryPart.filter(({ stockQty }) => {
    const hasQty = Number(stockQty) > 0;
    return hasQty;
  });

  const stockIsEnabled = useStockIsEnabled();

  const partCompareIds = [
    part?.partDetails.primaryPart.itemId,
    part?.partDetails.secondaryParts?.[0]?.itemId,
  ].filter(Boolean) as string[];

  const shouldRenderStockData = Boolean(stockIsEnabled && allStock?.length);

  const dlrStock = filterStock(allStock || [], "DLR");
  const dlrStockCount = countStock(dlrStock);
  const rltStock = filterStock(allStock || [], "RLT");

  const ribbon = useRibbon({ className: "tw-tire-tile__ribbon", index, part });

  const allowPricing = useAllowPartPricing(
    part,
    priceOption[0]?.pricingOption ?? null,
  );

  return (
    <figure
      className={clsx(
        "tw-tire-tile",
        part?.featured && featuredColumnClassNames[part.featuredPosition ?? 0]
      )}
    >
      <Rebates part={part} quantity={priceOption[0]?.pricingOption.quantity} />
      {ribbon}
      {part?.popular && (
        <div className="tw-tire-tile__popular-ribbon">
          {t("dealerTireTile.mostPopular")}
        </div>
      )}
      <div className={clsx("tw-tire-tile__content", ribbon && "pt-4 md:pt-0")}>
        <div className="p-4 md:p-6 xl:p-8 pr-1 md:pr-2 xl:pr-2 flex-1 grid grid-cols-3 relative">
          <Image
            alt="product image"
            className="tw-tire-tile__product_image"
            errorClassName="opacity-60 blur-[16px]"
            fallbackSrc={getPartFallbackSrc(partTypeTire)}
            src={fixTireImageUrl(part?.partDetails.primaryPart.tireImageLink)}
          />
          <div className="col-span-2 flex flex-col">
            <figcaption className="h-[5rem] mb-4 flex-1">
              <h2 className="text-xl lg:text-2xl">
                <Link
                  to={getProductModalHash(part?.partDetails.primaryPart.itemId)}
                >
                  <a className="hover:underline">
                    <Placeholder className="w-[8em]">
                      {getPartName(part, language)}
                    </Placeholder>
                  </a>
                </Link>
              </h2>
              <h3 className="text-xs lg:text-sm">
                <Placeholder className="w-[9em]" loading={!part}>
                  <span className="flex items-center gap-1">
                    <span className="whitespace-nowrap">
                      {!isStaggered && <>#{getTirePartNumber(part)} &ndash; </>}
                      {part?.partDetails.primaryPart.tireLoadRating}/
                      {part?.partDetails.primaryPart.tireSpeedIndex}/
                      {part?.partDetails.primaryPart.tirePlyRating}{" "}
                    </span>
                    <TireMetatagList
                      runFlat={
                        Number(part?.partDetails.primaryPart.tireRunFlat) === 1
                      }
                      tireCategoryTags={
                        part?.partDetails.primaryPart.tireCategoryTags || []
                      }
                      metatagList={metatagList}
                    />
                  </span>
                </Placeholder>
              </h3>
            </figcaption>
            <div
              className={clsx(
                // "grid grid-cols-2 gap-2 md:gap-4 justify-between items-center",
                "flex flex-wrap gap-2",
                "text-xs lg:text-sm max-w-[18rem]",
              )}
            >
              <PriceSelect
                isStaggered={isStaggered}
                keyClassName="font-light"
                onChange={setPiceOption}
                onReady={setPiceOption}
                orderItem={null}
                part={part}
                quantityInputClassName="bg-input"
                rowClassName="bg-paper bg-opacity-80 rounded-tw-full pr-1"
                ruleClassName="w-full invisible"
                shouldDisplayRule={isStaggered}
                totalClassName={clsx("text-lg w-full", !isStaggered && "mt-2")}
              />
            </div>
            {Boolean(dlrStock.length) && (
              <KeyValue className="tw-highlight max-w-min px-2 -mx-2 rounded-sm">
                {t("dealerTireTile.availableStock")}
                {formatCount(dlrStockCount)}
              </KeyValue>
            )}
          </div>
          {part && (
            <img
              alt="Manufacturer logo"
              src={part.partDetails.primaryPart.tireManufacturerImage}
            />
          )}
        </div>
        <div
          className={clsx(
            "tw-tire-tile__actions",
            isCheckedForCompare && "tw-tire-tile__actions--selected",
          )}
        >
          <CartButton
            className={clsx(
              "px-4 py-5 rounded-none",
              isStaggered ? "flex-1" : "md:pl-6 xl:pl-8",
            )}
            createPackageClassName="rounded-none"
            isStaggered={isStaggered}
            part={part}
            pricing={priceOption}
            vehicleId={resultParameters?.vehicleId || null}
            vehicleInfo={vehicleInfo}
          />
          <CompareButton
            disabled={!allowPricing}
            isCheckedForCompare={isCheckedForCompare}
            partIds={partCompareIds}
          />
        </div>
      </div>
      {shouldRenderStockData && part && (
        <TireStock
          allStock={allStock!}
          dlrStock={dlrStock}
          dropdownId={`tire-stock-${part?.partDetails.primaryPart.itemId}`}
          isRestricted={!isLoggedOn && stockIsEnabled}
          part={part}
          rltStock={rltStock}
        />
      )}
    </figure>
  );
};

// export const TiresTile = UncachedTiresTile;

export const TireTile = memo(UncachedTiresTile, (prevProps, nextProps) => {
  const entries = [
    [prevProps.index, nextProps.index],
    [prevProps.isCheckedForCompare, nextProps.isCheckedForCompare],
    [
      prevProps.part?.partDetails.primaryPart.itemId,
      nextProps.part?.partDetails.primaryPart.itemId,
    ],
    [prevProps.part?.featuredPosition, nextProps.part?.featuredPosition],
    [prevProps.resultParameters, nextProps.resultParameters],
    [prevProps.vehicleInfo, nextProps.vehicleInfo],
  ];

  const hasChanges = entries.some(([prev, next]) => prev !== next);

  return !hasChanges;
});
